import React from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";

const BVTSettingsFields = ({ bvtFields, setBvtFields }) => {
  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setBvtFields((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const blockedFields = new Set([
    "job_has_deferred_tax",
    "neg_insurance_percent",
    "neg_fee_percent",
    "shared_savings",
    "shared_savings_split_percent_to_arco",
    "max_shared_savings_amt",
  ]);

  const isFieldDisabled = (fieldName) =>
    bvtFields.contract_structure === "Lump Sum" && blockedFields.has(fieldName);

  return (
    <Box sx={{ width: "100%", py: 1 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Budget Variance Settings
      </Typography>

      {/* Two columns on md+, one on mobile */}
      <Box
        sx={{
          display: "grid",
          gap: 5,
          gridTemplateColumns: { xs: "1fr", md: "1.25fr 1fr" }, // left slightly wider
          alignItems: "start",
        }}
      >
        {/* LEFT COLUMN */}
        <Stack spacing={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="contract-structure-label">Contract Structure</InputLabel>
            <Select
              labelId="contract-structure-label"
              id="contract-structure-select"
              name="contract_structure"
              value={bvtFields.contract_structure || "GMP"}
              onChange={handleFieldChange}
              label="Contract Structure"
              fullWidth
            >
              <MenuItem value="GMP">GMP</MenuItem>
              <MenuItem value="Lump Sum">Lump Sum</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Negotiated Insurance Percent"
            name="neg_insurance_percent"
            value={bvtFields.neg_insurance_percent ?? ""}
            onChange={handleFieldChange}
            type="number"
            InputProps={{ inputProps: { min: -100, max: 100 } }}
            disabled={isFieldDisabled("neg_insurance_percent")}
            fullWidth
            InputLabelProps={{ sx: { whiteSpace: "normal" } }}
          />

          <TextField
            label="Negotiated Fee Percent"
            name="neg_fee_percent"
            value={bvtFields.neg_fee_percent ?? ""}
            onChange={handleFieldChange}
            type="number"
            InputProps={{ inputProps: { min: -100, max: 100 } }}
            disabled={isFieldDisabled("neg_fee_percent")}
            fullWidth
            InputLabelProps={{ sx: { whiteSpace: "normal" } }}
          />

          <TextField
            label="Max Shared Savings Amount"
            name="max_shared_savings_amt"
            value={bvtFields.max_shared_savings_amt ?? ""}
            onChange={handleFieldChange}
            type="number"
            disabled={isFieldDisabled("max_shared_savings_amt")}
            fullWidth
            InputLabelProps={{ sx: { whiteSpace: "normal" } }}
          />
        </Stack>

        {/* RIGHT COLUMN */}
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={bvtFields.job_has_deferred_tax || false}
                onChange={handleFieldChange}
                name="job_has_deferred_tax"
                color="primary"
                disabled={isFieldDisabled("job_has_deferred_tax")}
              />
            }
            label="Job Has Deferred Tax"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={bvtFields.shared_savings || false}
                onChange={handleFieldChange}
                name="shared_savings"
                color="primary"
                disabled={isFieldDisabled("shared_savings")}
              />
            }
            label="Shared Savings"
          />

          <TextField
            label="Shared Savings Split Percent to ARCO"
            name="shared_savings_split_percent_to_arco"
            value={bvtFields.shared_savings_split_percent_to_arco ?? ""}
            onChange={handleFieldChange}
            type="number"
            InputProps={{ inputProps: { min: 0, max: 100 } }}
            disabled={isFieldDisabled("shared_savings_split_percent_to_arco")}
            fullWidth
            InputLabelProps={{ sx: { whiteSpace: "normal" } }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default BVTSettingsFields;
